/**
 * UnlockToast + ToastQueue — PROG-2C.
 *
 * The single shared "you earned this" component. Tier 1 toasts auto-
 * dismiss after a hold, Tier 2/3 are sticky until tapped (Tier 3 reserved
 * for full-takeover moments later — for now they just hold longer + glow).
 *
 * Animation curves and durations track UI-Gameplan §1.5 motion vars.
 * Queue rules: max 3 visible at once, newest at top; older toasts collapse
 * into a "+N more" chip that opens the rest of the queue.
 */
import { useEffect, useState } from 'react';

export type ToastTier = 1 | 2 | 3;
export type ToastRarity = 1 | 2 | 3 | 4;
export type ToastKind = 'cosmetic' | 'achievement' | 'level' | 'mastery' | 'season' | 'currency' | 'unlock';

export interface UnlockToastSpec {
  id: string;
  tier: ToastTier;
  rarity: ToastRarity;
  kind: ToastKind;
  title: string;
  subtitle?: string;
  rewardChip?: string;
  /** Glyph or short emoji shown left of the title. */
  icon?: string;
  /** Time when the toast was queued (performance.now()). */
  enteredAt: number;
}

const HOLD_MS_BY_TIER: Record<ToastTier, number> = {
  1: 3200,
  2: 5400,
  3: 7200,
};
const VISIBLE_LIMIT = 3;

export function ToastQueue({ toasts, onDismiss }: { toasts: UnlockToastSpec[]; onDismiss: (id: string) => void }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? toasts : toasts.slice(0, VISIBLE_LIMIT);
  const hidden = toasts.length - visible.length;
  if (toasts.length === 0) return null;
  return (
    <div className="unlock-toast-stack" aria-live="polite">
      {visible.map((t) => (
        <UnlockToast key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
      {!showAll && hidden > 0 && (
        <button type="button" className="unlock-toast-more" onClick={() => setShowAll(true)}>
          +{hidden} more
        </button>
      )}
      {showAll && toasts.length > VISIBLE_LIMIT && (
        <button type="button" className="unlock-toast-more" onClick={() => setShowAll(false)}>
          collapse
        </button>
      )}
    </div>
  );
}

function UnlockToast({ toast, onDismiss }: { toast: UnlockToastSpec; onDismiss: () => void }) {
  useEffect(() => {
    const hold = HOLD_MS_BY_TIER[toast.tier];
    const t = window.setTimeout(onDismiss, hold);
    return () => window.clearTimeout(t);
  }, [toast.id, toast.tier, onDismiss]);
  return (
    <button
      type="button"
      className={`unlock-toast unlock-toast-tier-${toast.tier} unlock-toast-rarity-${toast.rarity} unlock-toast-kind-${toast.kind}`}
      onClick={onDismiss}
      aria-label={`${toast.title} — tap to dismiss`}
    >
      <span className="unlock-toast-icon" aria-hidden="true">{toast.icon ?? defaultIcon(toast.kind)}</span>
      <div className="unlock-toast-text">
        <div className="unlock-toast-title">{toast.title}</div>
        {toast.subtitle && <div className="unlock-toast-subtitle">{toast.subtitle}</div>}
      </div>
      {toast.rewardChip && <span className="unlock-toast-chip">{toast.rewardChip}</span>}
    </button>
  );
}

function defaultIcon(kind: ToastKind): string {
  switch (kind) {
    case 'level': return '⬆';
    case 'achievement': return '★';
    case 'mastery': return '◆';
    case 'cosmetic': return '✦';
    case 'season': return '☀';
    case 'currency': return '+';
    case 'unlock':
    default: return '✦';
  }
}

/**
 * Helper hook: caller pushes specs as raw payloads (no enteredAt, no id),
 * the hook assigns ids + timestamps and exposes the queue + dismiss.
 */
export function useToastQueue() {
  const [toasts, setToasts] = useState<UnlockToastSpec[]>([]);
  const push = (spec: Omit<UnlockToastSpec, 'id' | 'enteredAt'>) => {
    setToasts((cur) => {
      // Dedupe on TITLE only across an 8s window. Three paths can fire
      // the same reward (legacy ACHIEVEMENTS row, V2 ACHIEVEMENTS_V2 row,
      // cosmetic-diff watcher) and they sometimes use different `kind`
      // values for the same human-visible reward, which slipped past the
      // earlier kind-aware dedup. Title is the user-perceived identity.
      const titleKey = spec.title.trim().toLowerCase();
      const now = performance.now();
      for (const t of cur) {
        if (t.title.trim().toLowerCase() === titleKey && now - t.enteredAt < 8000) {
          return cur;
        }
      }
      const id = `${spec.kind}-${spec.title}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const next: UnlockToastSpec = { ...spec, id, enteredAt: now };
      return [next, ...cur];
    });
  };
  const dismiss = (id: string) => setToasts((cur) => cur.filter((t) => t.id !== id));
  const clear = () => setToasts([]);
  return { toasts, push, dismiss, clear };
}
